import React from 'react';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import EditIcon from '@material-ui/icons/Edit';
import { JobSheetHeaderData, JobSheetTab } from 'models/JobDetails';
import { AppointmentStatus } from 'models/Appointment';
import { Tabs, TabPanels, TabsProvider } from 'components/Tabs/Tabs';
import CustomButton, { DefaultButton } from 'components/Button';
import AtRiskButton from 'components/AtRiskButton/AtRiskButton';
import OpenScheduleButton from 'components/OpenScheduleButton/OpenScheduleButton';
import Typography from 'components/Typography';
import { ToolbarMinHeight } from 'styles/types';
import JobSheetHeader from 'components/JobDetailsSheet/JobSheetHeader/JobSheetHeader';
import {
  CanCancelStatus,
  useCanCancel
} from 'components/JobDetailsSheet/useCanCancel';
import WorkflowPane from 'components/JobDetailsSheet/WorkflowPane/WorkflowPane';
import { HistoryPane } from 'components/JobDetailsSheet/HistoryPane/HistoryPane';
import {
  JobMovementEngineersState,
  useJobMovementEngineers
} from 'components/JobDetailsSheet/HistoryPane/useJobMovementEngineers';
import { NewJobSheetPanel, TAB_COLUMN_WIDTH_UNITS } from './NewJobSheetPanel';
import EmptyPanel from './EmptyPanel';

const AUDIT_PANE_WIDTH = 603;
const AUDIT_COLUMN_WIDTHS = [158, 276, 169];

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3.5)
  },
  pageTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    color: theme.palette.aegis.semantic.message.black
  },
  editButton: {
    padding: theme.spacing(1),
    minWidth: 'auto',
    '& .MuiButton-startIcon': {
      margin: theme.spacing(0, 1, 0, 0),
      '& .MuiSvgIcon-root': {
        fontSize: theme.spacing(3)
      }
    }
  },
  cardShell: {
    position: 'relative',
    height: `calc(100vh - ${(theme.mixins.toolbar[
      theme.breakpoints.up('sm')
    ] as ToolbarMinHeight).minHeight + theme.spacing(4)}px)`,
    viewTimelineName: '--card-shell'
  },
  card: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: '100%',
    paddingBottom: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.common.white,
    boxShadow:
      '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 3px 4px rgba(0, 0, 0, 0.12), 0px 1px 5px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    '@supports (animation-timeline: view())': {
      animation: '$growWithScroll linear both',
      animationTimeline: '--card-shell',
      animationRange: `cover ${theme.spacing(2)}px contain ${theme.spacing(
        2
      )}px`
    }
  },
  '@keyframes growWithScroll': {
    from: {
      maxHeight: 0
    },
    to: {
      maxHeight: '100%'
    }
  },
  cardHeader: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'flex-start',
    justifyContent: 'space-between'
  },
  chips: {
    padding: theme.spacing(2),
    '& > div': {
      marginBottom: 0
    },
    '& .MuiChip-root': {
      height: theme.spacing(3.5),
      marginRight: 0,
      fontSize: theme.typography.body?.fontSize
    },
    '& .MuiChip-icon': {
      fontSize: theme.spacing(3),
      margin: theme.spacing(0, -0.5, 0, 0.5),
      color: theme.palette.aegis.semantic.message.dark
    }
  },
  actions: {
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1, 1, 2)
  },
  actionButton: {
    boxShadow:
      '0px 0px 2px rgba(0, 0, 0, 0.14), 0px 2px 2px rgba(0, 0, 0, 0.12), 0px 1px 3px rgba(0, 0, 0, 0.2)'
  },
  atRiskButton: {
    color: theme.palette.aegis.semantic.warning.base,
    '&, &:hover': {
      borderColor: theme.palette.aegis.semantic.warning.base
    }
  },
  tabs: {
    flexShrink: 0
  },
  tabContent: {
    minHeight: 0,
    overflowY: 'auto'
  },
  tabPane: {
    width: theme.spacing(TAB_COLUMN_WIDTH_UNITS)
  },
  auditPane: {
    width: AUDIT_PANE_WIDTH,
    '& .MuiTableCell-root': {
      borderBottom: 'none'
    },
    '& thead .MuiTableCell-root': {
      textTransform: 'none'
    }
  }
}));

interface TabContentProps {
  tab: JobSheetTab;
  status: AppointmentStatus;
  jobMovementEngineers: JobMovementEngineersState;
}

const TabContent = ({ tab, status, jobMovementEngineers }: TabContentProps) => {
  const classes = useStyles();

  if ('workflowSections' in tab) {
    return (
      <WorkflowPane
        sections={tab.workflowSections}
        status={status}
        className={classes.tabPane}
        openImagesInNewTab
      />
    );
  }

  if ('previousAbortEntries' in tab) {
    if (tab.previousAbortEntries.length === 0) {
      return <EmptyPanel message="No previous aborts found" />;
    }

    return <NewJobSheetPanel rows={tab.previousAbortEntries} />;
  }

  if ('auditEntries' in tab) {
    return (
      <HistoryPane
        auditData={tab.auditEntries}
        engineers={jobMovementEngineers.engineers}
        isLoading={jobMovementEngineers.isLoading}
        columnWidths={AUDIT_COLUMN_WIDTHS}
        className={classes.auditPane}
      />
    );
  }

  return <NewJobSheetPanel rows={tab.rows} sections={tab.sections} />;
};

export interface NewJobSheetProps {
  tabsData: JobSheetTab[];
  headerData: JobSheetHeaderData;
  openEditModal: () => void;
  openAbortModal: () => void;
  openCancelModal: () => void;
  jobId: string;
  scheduleDate: string;
  patch?: string;
  reloadJobSheet: () => void;
  assignedEngineerId?: string;
}

const hideAtRiskButtonCases = [
  AppointmentStatus.ABORTED,
  AppointmentStatus.CANCELLED,
  AppointmentStatus.COMPLETED
];

export default function NewJobSheet(props: NewJobSheetProps) {
  const {
    tabsData,
    headerData,
    openEditModal,
    openAbortModal,
    openCancelModal,
    jobId,
    scheduleDate,
    patch,
    reloadJobSheet,
    assignedEngineerId
  } = props;
  const classes = useStyles();

  const jobMovementEngineers = useJobMovementEngineers(
    tabsData.flatMap(tab => ('auditEntries' in tab ? tab.auditEntries : []))
  );

  const canBeCancelledOrAborted = useCanCancel(jobId);
  const canAbort = canBeCancelledOrAborted === CanCancelStatus.ABORTABLE;
  const canCancel = canBeCancelledOrAborted === CanCancelStatus.CANCELLABLE;
  const showAtRiskButton =
    !hideAtRiskButtonCases.includes(headerData.status) || headerData.atRisk;

  return (
    <div className={classes.root} data-testid="newJobSheet">
      <div className={classes.pageHeader}>
        <div className={classes.pageTitle}>
          <Typography variant="h1">Job Sheet</Typography>
          <Typography>
            View the booking, customer and account details for this job
          </Typography>
        </div>
        <CustomButton
          className={classes.editButton}
          color="primary"
          size="medium"
          startIcon={<EditIcon />}
          onClick={openEditModal}
          testId="editJobButton"
        >
          Edit Job
        </CustomButton>
      </div>
      <TabsProvider tabs={tabsData.map(({ label }) => ({ label }))}>
        <div className={classes.cardShell}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.chips}>
                <JobSheetHeader
                  headerData={headerData}
                  assignedEngineerId={assignedEngineerId}
                />
              </div>
              <div className={classes.actions}>
                {canAbort && (
                  <DefaultButton
                    className={classes.actionButton}
                    size="medium"
                    onClick={openAbortModal}
                    testId="jobDetailsAbortButton"
                  >
                    Abort
                  </DefaultButton>
                )}
                {canCancel && (
                  <DefaultButton
                    className={classes.actionButton}
                    size="medium"
                    onClick={openCancelModal}
                    testId="jobDetailsCancelButton"
                  >
                    Cancel
                  </DefaultButton>
                )}
                {showAtRiskButton && (
                  <AtRiskButton
                    appointmentId={jobId}
                    atRisk={headerData.atRisk}
                    className={clsx(classes.actionButton, classes.atRiskButton)}
                    data-testid="atRiskButton"
                    onSuccess={reloadJobSheet}
                    variant="outlined"
                    size="medium"
                  />
                )}
                {patch && scheduleDate && (
                  <OpenScheduleButton
                    scheduleDate={scheduleDate}
                    patch={patch}
                    jobId={jobId}
                    assignedEngineerId={assignedEngineerId}
                    className={classes.actionButton}
                    data-testid="openScheduleButton"
                    variant="outlined"
                    size="medium"
                  />
                )}
              </div>
            </div>
            <Tabs className={classes.tabs} />
            <div className={classes.tabContent}>
              <TabPanels>
                {tabsData.map(tab => (
                  <TabContent
                    key={tab.label}
                    tab={tab}
                    status={headerData.status}
                    jobMovementEngineers={jobMovementEngineers}
                  />
                ))}
              </TabPanels>
            </div>
          </div>
        </div>
      </TabsProvider>
    </div>
  );
}
